import { Link } from 'react-router-dom';
import { ArrowRightIcon, MessageCircleIcon } from 'lucide-react';
import { RoutineCard } from '../shared/RoutineCard';
import { ShopFaq } from '../shared/ShopFaq';
import { SubscriptionBlock } from '../shared/SubscriptionBlock';
import { pageActions } from '../../data/pageActions';
import { HeadingText } from '../shared/HeadingText';
import { PageHero } from '../shared/PageHero';
import { useCatalog } from '../../contexts/CatalogContext';
import { useOverlayActions } from '../../contexts/StoreContext';
import { founder, salon } from '../../data/content';
import { SALON_CODE } from '../../data/codes';
import { usePages } from '../../contexts/SiteContext';
import { fill } from '../../utils/copy';
import { mediaOr } from '../../core/rules/media';
import { shopLabels } from '../../data/shopLabels';
import { btnPrimaryLg, btnSecondaryLg } from '../../ui/styles';
import { quietLink, sectionHeading } from '../../ui/sections';
import { Section } from '../shared/Section';
import { ClosingBand } from '../shared/ClosingBand';
import { plural } from '../../core/format/text';
import { RoutineCompare } from './RoutineCompare';
import { RoutineAnchor } from './RoutineAnchor';
import { GiftTierList } from './GiftTierList';
import { anchorRoutine, ROUTINES_FAQ_COUNT } from './routineRules';

/**
 * Protocollen: de pagina met de hoogste orderwaarde. Volgorde = de vragen van een bezoeker
 * (docs/VORMGEVING.md §3): wat is een protocol (hero, sand) · hoe werkt het en wat krijg ik
 * erbij (shell) · het complete protocol (blush) · of begin bij je klacht, en vergelijk (sand) ·
 * abonnement (kort) · vragen · al klant in de salon? (blush; de footer is klei).
 */
export function Routines() {
  const pages = usePages();
  const {
    openQuiz
  } = useOverlayActions();
  const {
    routines,
    routineTotals
  } = useCatalog();
  const copy = pages.routines;
  const complete = anchorRoutine(routines, r => routineTotals(r).price);
  const others = routines.filter(r => r.id !== complete?.id);
  const heroMedia = mediaOr(copy.hero.media, {
    image: complete?.image || founder.withProducts,
    alt: complete ? `De producten van ${complete.name} op een rij` : `${founder.name} met de producten waar ze mee werkt`
  });
  return <>
      {/* Hero: zelfde opbouw als salon en over. In het beeld: wie het opstelt, en wat je meekrijgt. */}
      <PageHero titleId="routines-title" eyebrow={copy.hero.eyebrow ? fill(copy.hero.eyebrow) : undefined} title={<HeadingText heading={copy.hero.heading} accentBlock data-id="element-8340" data-mp="25t6ukkk1adu5" />} body={copy.hero.body ? fill(copy.hero.body) : undefined} media={heroMedia} byline={{
      lead: <>
              Persoonlijk huidplan, opgesteld door <span className="text-ink" data-id="element-8341" data-mp="2jlcy97602scp">{founder.firstName}</span>
            </>,
      sub: `${plural(routines.length, 'protocol', 'protocollen')} · 100% vegan`
    }} actions={/* Hoofdknop: naar de protocollen zelf. Wie nog twijfelt, doet eerst de huidtest. */
    <>
            <a href={complete ? '#anchor-routine' : '#other-routines'} className={btnPrimaryLg} data-id="element-8342" data-mp="hy0avz9xagm1">
              {pageActions.startProtocol}
            </a>
            {copy.hero.cta && <button type="button" onClick={openQuiz} aria-haspopup="dialog" className={btnSecondaryLg} data-id="element-8343" data-mp="8040xasq1rpi">
                {fill(copy.hero.cta)}
              </button>}
          </>} data-id="element-8339" data-mp="20wpxv1yrqr5g" />

      {/* Hoe het werkt: eerst uitleggen, dan pas aanbieden. Met de cadeaus ernaast. */}
      <Section tone="shell" pad="compact" labelledBy="routines-how" contained={false} data-id="element-8344" data-mp="vpoo65vwrb4l">
        <div className="mx-auto grid max-w-content gap-14 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-8345" data-mp="qx57h01xbzvk">
          <div className="lg:col-span-8" data-id="element-8346" data-mp="lzlv65osdx69">
            <h2 id="routines-how" className="type-sub text-ink" data-id="element-8347" data-mp="lwrbigdlkgdw">
              {fill(copy.howHeading)}
            </h2>
            <ol className="mt-8 grid gap-8 sm:grid-cols-3 sm:gap-6" data-id="element-8348" data-mp="1enhqb5ypjh0x">
              {copy.steps.map((s, i) => <li key={s.title} className="border-t border-ink/15 pt-5" data-id="element-8349" data-mp="z5rieu2m0s4p" data-mpi={i}>
                  {/* De lijst is al genummerd voor schermlezers; de volgorde is hier de inhoud. */}
                  <span aria-hidden="true" className="type-sub leading-none text-clay" data-id="element-8350" data-mp="142883spgjejw">
                    {i + 1}
                  </span>
                  <h3 className="mt-4 type-name text-ink" data-id="element-8351" data-mp="u5lp79goyxki">{fill(s.title)}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-muted" data-id="element-8352" data-mp="1hw9acwfmbjp0">{fill(s.body)}</p>
                </li>)}
            </ol>
          </div>

          <div className="lg:col-span-4" data-id="element-8353" data-mp="1muauzlsozr2l">
            <h2 className="type-sub text-ink" data-id="element-8354" data-mp="186pp5k4fndqz">{fill(copy.gifts.heading)}</h2>
            <GiftTierList className="mt-8" data-id="element-8355" data-mp="1d59cnoyocxlg" />
            <a href={salon.whatsapp} target="_blank" rel="noreferrer" className="mt-4 flex items-start gap-2 text-[14px] leading-relaxed text-muted underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline" data-id="element-8356" data-mp="1d47qopg4eqoy">
              <MessageCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-8357" data-mp="3qao2dqywva11" />
              {fill(copy.gifts.note)}
              <span className="sr-only" data-id="element-8358" data-mp="3w4qc0xes6xq">{shopLabels.viaWhatsApp}</span>
            </a>
          </div>
        </div>
      </Section>

      {complete && <RoutineAnchor routine={complete} data-id="element-8359" data-mp="3v37iaul2mh90" />}

      {/* Of begin bij je klacht. Met maar één protocol online is er geen "overige": dan alleen vergelijken (vanaf twee). */}
      {(others.length > 0 || routines.length > 1) && <Section tone="sand" labelledBy={others.length > 0 ? 'other-routines' : undefined} label={copy.compareHeading} contained={false} data-id="element-8360" data-mp="32g4je1chg4i8">
          <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-8361" data-mp="3cbodci7hkt4c">
            {others.length > 0 && <>
                <div className="grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-16" data-id="element-8362" data-mp="vm97zrtsafv9">
                  <h2 id="other-routines" className={`${sectionHeading} text-ink lg:col-span-7`} data-id="element-8363" data-mp="rmqlwsq87yqu">
                    <HeadingText heading={copy.others.heading} data-id="element-8364" data-mp="3kh8odqbk3dhi" />
                  </h2>
                  {copy.others.body && <p className="max-w-md text-[17px] leading-relaxed text-muted lg:col-span-5" data-id="element-8365" data-mp="3pehc06fh49q5">{fill(copy.others.body)}</p>}
                </div>

                <ul className={`mt-14 grid gap-6 md:grid-cols-2 ${others.length >= 3 ? 'lg:grid-cols-3' : ''}`} data-id="element-8366" data-mp="3ucsjwbm7ka5q">
                  {others.map(r => <li key={r.id} className="h-full bg-shell p-7 lg:p-8" data-id="element-8367" data-mp="31r0ulytqxzdz">
                      <RoutineCard routine={r} data-id="element-8368" data-mp="36oj3jp7rkelz" />
                    </li>)}
                </ul>
              </>}

            <RoutineCompare heading={fill(copy.compareHeading)} data-id="element-8369" data-mp="3apcqogddtpdt" />

            {copy.smallStart.body && <div className="mt-12 flex max-w-3xl flex-wrap items-baseline gap-x-6 gap-y-3" data-id="element-8370" data-mp="3finro0vijbxt">
                <p className="text-[17px] leading-relaxed text-muted" data-id="element-8371" data-mp="2iz7yvwnam02k">{fill(copy.smallStart.body)}</p>
                {copy.smallStart.shopCta && <Link to="/webshop" className={`${quietLink} text-ink`} data-id="element-8372" data-mp="2fi2d1qeqcwf5">
                    {fill(copy.smallStart.shopCta)}
                    <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8373" data-mp="2ajr21ccp8fvc" />
                  </Link>}
                {/* De huidtest staat al in de opening: hier niet nog eens. */}
              </div>}
          </div>
        </Section>}

      {/* Abonnement en vragen: dezelfde blokken als op de webshop, hier in de korte versie. */}
      <SubscriptionBlock size="short" data-id="element-8374" data-mp="1rqwz5gscoe7h" />

      <ShopFaq limit={ROUTINES_FAQ_COUNT} data-id="element-8375" data-mp="1rpva28ei1r6r" />

      {/* Voor klanten van de salon: de salon als bron, op blush. Niet op klei: de footer is al klei. */}
      <ClosingBand tone="blush" titleId="salon-client" title={<HeadingText heading={copy.salonClient.heading} data-id="element-8377" data-mp="1drx8y4p6vr97" />} body={copy.salonClient.body ? fill(copy.salonClient.body, {
      code: SALON_CODE
    }) : undefined} actions={copy.salonClient.cta && <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={btnPrimaryLg} data-id="element-8378" data-mp="2wpjvr0y3z6tp">
              {fill(copy.salonClient.cta)}
              <span className="sr-only" data-id="element-8379" data-mp="31o3giy0yuckh">{shopLabels.viaWhatsApp}</span>
            </a>} data-id="element-8376" data-mp="1is2v55cowljw" />
    </>;
}